import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import moment from "moment";
import { axiosInstance } from "../../../utils/networking";
import {
  BulkCloseBatch,
  BulkCloseBatchesResponse,
  BulkCloseUndoResult,
} from "../../../interfaces";
import { plural } from "./caseManagementUtils";

const PRIMARY = "#9E1F63FF";
const CARD_BORDER = "1px solid #ececf0";
const BATCH_LIMIT = 10;

const buttonSx = { textTransform: "none", fontWeight: 600, borderRadius: 2 };

interface BatchHistoryProps {
  /** Bumped by the page after any write, to refetch. */
  refreshKey: number;
  /** Expand the panel (e.g. right after a bulk close). */
  expandKey: number;
  /** Called after an Undo succeeds so the page refreshes its table. */
  onUndone: (result: BulkCloseUndoResult) => void;
  onForbidden: () => void;
}

// "Recent batches" panel over GET /cases/bulk-close/batches, with Undo
// (POST /cases/bulk-close/undo) behind a confirm dialog.
const BatchHistory = ({ refreshKey, expandKey, onUndone, onForbidden }: BatchHistoryProps) => {
  const [batches, setBatches] = useState<BulkCloseBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<BulkCloseBatch | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [undoError, setUndoError] = useState<string | null>(null);

  const requestIdRef = useRef(0);
  useEffect(() => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    axiosInstance
      .get(`/cases/bulk-close/batches?limit=${BATCH_LIMIT}`)
      .then((res) => {
        if (requestId !== requestIdRef.current) return;
        const data: BulkCloseBatchesResponse = res.data || {};
        setBatches(Array.isArray(data.batches) ? data.batches : []);
        setError(null);
      })
      .catch((err) => {
        if (requestId !== requestIdRef.current) return;
        if (err?.response?.status === 403) {
          onForbidden();
          return;
        }
        console.error("Failed to load bulk-close batches:", err);
        setError("Could not load recent batches.");
      })
      .finally(() => {
        if (requestId !== requestIdRef.current) return;
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    if (expandKey > 0) setOpen(true);
  }, [expandKey]);

  const undo = () => {
    if (!confirm) return;
    setUndoing(true);
    setUndoError(null);
    axiosInstance
      .post(`/cases/bulk-close/undo`, { batch_id: confirm.batch_id })
      .then((res) => {
        const result: BulkCloseUndoResult = res.data || {
          batch_id: confirm.batch_id,
          reopened: 0,
          skipped: 0,
        };
        setConfirm(null);
        onUndone(result);
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          setConfirm(null);
          onForbidden();
          return;
        }
        setUndoError(
          err?.response?.data?.message ||
            "Undo failed. Some cases may already have been reopened — refresh and check.",
        );
      })
      .finally(() => setUndoing(false));
  };

  const latest = batches[0];
  const summary = loading
    ? "Loading…"
    : error
      ? error
      : latest
        ? `Last: ${plural(latest.closed, "case")} closed ${moment(latest.created_at).fromNow()}${
            latest.actor_name ? ` by ${latest.actor_name}` : ""
          }${latest.undone ? " (undone)" : ""}`
        : "No bulk closes yet";

  return (
    <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", mb: 2 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ px: 2, py: 1.25, flexWrap: "wrap" }}
        useFlexGap
      >
        <Typography sx={{ fontWeight: 700, color: "#2c2c34" }}>Recent batches</Typography>
        <Typography variant="body2" sx={{ color: error ? "#c62828" : "#8a8a93", flex: 1 }}>
          {summary}
        </Typography>
        {batches.length ? (
          <Button size="small" onClick={() => setOpen((o) => !o)} sx={{ ...buttonSx, color: PRIMARY }}>
            {open ? "Hide" : `Show ${batches.length}`}
          </Button>
        ) : null}
      </Stack>
      <Collapse in={open && batches.length > 0} unmountOnExit>
        <Box sx={{ overflowX: "auto", borderTop: CARD_BORDER }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, color: "#2c2c34" } }}>
                <TableCell>When</TableCell>
                <TableCell>By</TableCell>
                <TableCell align="right">Closed</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {batches.map((b) => (
                <TableRow key={b.batch_id}>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Tooltip title={moment(b.created_at).format("MMM D, YYYY h:mm A")}>
                      <span>{moment(b.created_at).format("MMM D, YYYY")}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{b.actor_name || "—"}</TableCell>
                  <TableCell align="right">{(b.closed || 0).toLocaleString()}</TableCell>
                  <TableCell sx={{ maxWidth: 420 }}>
                    <Typography
                      variant="body2"
                      title={b.reason}
                      sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                      {b.reason || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {b.undone ? (
                      <Chip size="small" label="Undone" sx={{ fontWeight: 600 }} />
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<UndoIcon fontSize="small" />}
                        onClick={() => {
                          setUndoError(null);
                          setConfirm(b);
                        }}
                        sx={{ ...buttonSx, color: PRIMARY, borderColor: PRIMARY }}
                      >
                        Undo
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Collapse>

      <Dialog
        open={!!confirm}
        onClose={() => !undoing && setConfirm(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, p: 3 } }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            bgcolor: "#fbf5e6",
            color: "#8a6d00",
            display: "grid",
            placeItems: "center",
            mb: 2,
          }}
        >
          <WarningAmberRoundedIcon />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>Undo this bulk close?</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Reopens the {confirm ? plural(confirm.closed, "case") : "cases"} closed in this batch
          {confirm ? ` on ${moment(confirm.created_at).format("MMM D, YYYY")}` : ""} that are
          still closed. Each gets a “Case Reopened” activity.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Reminders cancelled by the close are <b>not</b> restored.
        </Typography>
        {undoError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {undoError}
          </Alert>
        ) : null}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setConfirm(null)}
            disabled={undoing}
            sx={{ ...buttonSx, color: "text.primary", borderColor: "divider" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={undo}
            disabled={undoing}
            startIcon={undoing ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
            sx={{ ...buttonSx, backgroundColor: PRIMARY, "&:hover": { backgroundColor: "#7d184e" } }}
          >
            {undoing ? "Reopening…" : "Undo batch"}
          </Button>
        </Box>
      </Dialog>
    </Paper>
  );
};

export default BatchHistory;
